import { useEffect, useState } from "react";
import {
    Coins,
    TrendingUp,
    Target,
    Wallet,
    Plus,
    Trash2,
    Check,
    Clock,
    Send,
    Compass,
} from "lucide-react";
import { cockpit } from "@/lib/cockpitAuth";

const EUR = new Intl.NumberFormat("fr-FR", {
    style: "currency",
    currency: "EUR",
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
});
export const formatEUR = (n) => EUR.format(Number(n) || 0);

const STATUS_META = {
    pending: { l: "À émettre", c: "#f4ecd8", bg: "rgba(244,236,216,0.08)", Icon: Clock },
    sent: { l: "Émise", c: "#8fa3c4", bg: "rgba(143,163,196,0.12)", Icon: Send },
    paid: { l: "Payée", c: "#d4af37", bg: "rgba(212,175,55,0.12)", Icon: Check },
};

// ─────────────────────────────────────────────────────────────
// FinancialPanel — Per-dossier invoice tracking + KPIs
// ─────────────────────────────────────────────────────────────

export function FinancialPanel({ dossier, onDossierUpdate }) {
    const dossierId = dossier.id;
    const [invoices, setInvoices] = useState(dossier.invoices || []);
    const [busy, setBusy] = useState(false);
    const [draft, setDraft] = useState({
        label: "",
        amount: "",
        date: new Date().toISOString().slice(0, 10),
        status: "pending",
        note: "",
        category: "",
    });
    const [editId, setEditId] = useState(null);

    useEffect(() => {
        setInvoices(dossier.invoices || []);
    }, [dossier]);

    const realized = invoices
        .filter((i) => i.status === "paid")
        .reduce((s, i) => s + Number(i.amount || 0), 0);
    const pending = invoices
        .filter((i) => i.status === "pending" || i.status === "sent")
        .reduce((s, i) => s + Number(i.amount || 0), 0);
    const target = Number(dossier.revenue_target || 0);
    const totalEngaged = realized + pending;
    const progressPct = target > 0
        ? Math.min(100, (totalEngaged / target) * 100)
        : 0;

    const reload = async () => {
        const { data } = await cockpit.get(`/dossiers/${dossierId}`);
        setInvoices(data.invoices || []);
        if (onDossierUpdate) onDossierUpdate(data);
    };

    const submitInvoice = async (e) => {
        e.preventDefault();
        if (!draft.label.trim() || !draft.amount) return;
        setBusy(true);
        try {
            const payload = {
                label: draft.label.trim(),
                amount: Number(draft.amount),
                date: draft.date,
                status: draft.status,
                note: draft.note,
                category: draft.category || "",
            };
            if (editId) {
                await cockpit.put(
                    `/dossiers/${dossierId}/invoices/${editId}`,
                    payload,
                );
            } else {
                await cockpit.post(
                    `/dossiers/${dossierId}/invoices`,
                    payload,
                );
            }
            setDraft({
                label: "",
                amount: "",
                date: new Date().toISOString().slice(0, 10),
                status: "pending",
                note: "",
                category: "",
            });
            setEditId(null);
            await reload();
        } finally {
            setBusy(false);
        }
    };

    const startEdit = (inv) => {
        setEditId(inv.id);
        setDraft({
            label: inv.label,
            amount: String(inv.amount),
            date: (inv.date || "").slice(0, 10),
            status: inv.status,
            note: inv.note || "",
            category: inv.category || "",
        });
    };

    const cancelEdit = () => {
        setEditId(null);
        setDraft({
            label: "",
            amount: "",
            date: new Date().toISOString().slice(0, 10),
            status: "pending",
            note: "",
            category: "",
        });
    };

    const removeInvoice = async (id) => {
        if (!window.confirm("Supprimer cette facture ?")) return;
        await cockpit.delete(`/dossiers/${dossierId}/invoices/${id}`);
        await reload();
    };

    const setStatusInline = async (inv, status) => {
        await cockpit.put(`/dossiers/${dossierId}/invoices/${inv.id}`, {
            status,
        });
        await reload();
    };

    return (
        <section
            data-testid="cockpit-dossier-finance"
            className="border-t border-white/[0.06] py-12 mt-6"
        >
            <div className="flex items-end justify-between mb-8 flex-wrap gap-4">
                <div>
                    <div className="overline text-[#d4af37]/70 mb-3">
                        Suivi financier
                    </div>
                    <h2
                        className="font-serif text-[24px] text-[#f4ecd8]"
                        style={{ fontWeight: 400 }}
                    >
                        Honoraires &amp; chiffre d'affaires
                    </h2>
                </div>
                {target > 0 && (
                    <div className="text-right">
                        <div className="text-[10px] tracking-[0.28em] uppercase text-[#d4af37]/70 mb-1">
                            Atteinte de la cible
                        </div>
                        <div
                            className="font-serif text-[28px] text-[#f4ecd8]"
                            style={{ fontWeight: 300 }}
                        >
                            {progressPct.toFixed(1)}%
                        </div>
                    </div>
                )}
            </div>

            {/* KPI strip */}
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-px bg-white/[0.06] border border-white/[0.06] mb-10">
                <Kpi
                    label="Honoraires encaissés"
                    sub="Factures payées"
                    value={realized}
                    accent="#d4af37"
                    Icon={Coins}
                    testid="kpi-realized"
                />
                <Kpi
                    label="Chiffre d'affaires en cours"
                    sub="Émises ou à émettre"
                    value={pending}
                    accent="#8fa3c4"
                    Icon={TrendingUp}
                    testid="kpi-pending"
                />
                <Kpi
                    label="Chiffre d'affaires réalisé"
                    sub="Encaissé + en cours"
                    value={totalEngaged}
                    accent="#f4ecd8"
                    Icon={Wallet}
                    testid="kpi-engaged"
                />
                <Kpi
                    label="Chiffre d'affaires cible"
                    sub="Engagement projeté"
                    value={target}
                    accent="#e8c46b"
                    Icon={Target}
                    testid="kpi-target"
                />
            </div>

            {/* Progress bar towards target */}
            {target > 0 && (
                <div className="mb-12">
                    <div className="flex justify-between text-[10px] tracking-[0.28em] uppercase text-[#f4ecd8]/45 mb-2">
                        <span>0 €</span>
                        <span>{formatEUR(target)}</span>
                    </div>
                    <div className="relative h-2 bg-white/[0.05] overflow-hidden">
                        <div
                            className="absolute inset-y-0 left-0"
                            style={{
                                width: `${target > 0 ? (realized / target) * 100 : 0}%`,
                                backgroundColor: "#d4af37",
                            }}
                        />
                        <div
                            className="absolute inset-y-0"
                            style={{
                                left: `${target > 0 ? (realized / target) * 100 : 0}%`,
                                width: `${target > 0 ? (pending / target) * 100 : 0}%`,
                                backgroundColor: "#8fa3c4",
                            }}
                        />
                    </div>
                </div>
            )}

            {/* Invoice form */}
            <form
                onSubmit={submitInvoice}
                data-testid="invoice-form"
                className="bg-[#070d18] border border-white/[0.08] p-6 lg:p-8 mb-10"
            >
                <div className="overline text-[#d4af37]/80 mb-5">
                    {editId ? "Modifier la facture" : "Ajouter une facture / honoraire"}
                </div>
                <div className="grid sm:grid-cols-2 lg:grid-cols-5 gap-4">
                    <div className="lg:col-span-2">
                        <label className="block text-[10px] tracking-[0.3em] uppercase text-[#d4af37]/70 mb-2">
                            Libellé
                        </label>
                        <input
                            data-testid="invoice-label"
                            value={draft.label}
                            onChange={(e) => setDraft({ ...draft, label: e.target.value })}
                            placeholder="Acompte signature, Closing, etc."
                            required
                            className="w-full bg-transparent border-b border-white/15 focus:border-[#d4af37] outline-none py-2 text-[14px] font-serif text-[#f4ecd8]"
                        />
                    </div>
                    <div>
                        <label className="block text-[10px] tracking-[0.3em] uppercase text-[#d4af37]/70 mb-2">
                            Montant HT (€)
                        </label>
                        <input
                            data-testid="invoice-amount"
                            type="number"
                            step="0.01"
                            min="0"
                            value={draft.amount}
                            onChange={(e) => setDraft({ ...draft, amount: e.target.value })}
                            placeholder="0.00"
                            required
                            className="w-full bg-transparent border-b border-white/15 focus:border-[#d4af37] outline-none py-2 text-[14px] font-serif text-[#f4ecd8] tabular-nums"
                        />
                    </div>
                    <div>
                        <label className="block text-[10px] tracking-[0.3em] uppercase text-[#d4af37]/70 mb-2">
                            Date
                        </label>
                        <input
                            data-testid="invoice-date"
                            type="date"
                            value={draft.date}
                            onChange={(e) => setDraft({ ...draft, date: e.target.value })}
                            className="w-full bg-transparent border-b border-white/15 focus:border-[#d4af37] outline-none py-2 text-[14px] font-serif text-[#f4ecd8]"
                        />
                    </div>
                    <div>
                        <label className="block text-[10px] tracking-[0.3em] uppercase text-[#d4af37]/70 mb-2">
                            Statut
                        </label>
                        <select
                            data-testid="invoice-status"
                            value={draft.status}
                            onChange={(e) => setDraft({ ...draft, status: e.target.value })}
                            className="w-full bg-[#0b1220] border-b border-white/15 focus:border-[#d4af37] outline-none py-2 text-[14px] font-serif text-[#f4ecd8]"
                        >
                            <option value="pending">À émettre</option>
                            <option value="sent">Émise</option>
                            <option value="paid">Payée</option>
                        </select>
                    </div>
                </div>
                <div className="mt-5 grid sm:grid-cols-2 gap-5">
                    <div>
                        <label className="block text-[10px] tracking-[0.3em] uppercase text-[#d4af37]/70 mb-2">
                            Catégorie d'honoraires
                        </label>
                        <select
                            data-testid="invoice-category"
                            value={draft.category || ""}
                            onChange={(e) => setDraft({ ...draft, category: e.target.value })}
                            className="w-full bg-[#0b1220] border-b border-white/15 focus:border-[#d4af37] outline-none py-2 text-[14px] font-serif text-[#f4ecd8]"
                        >
                            <option value="">Non catégorisé</option>
                            <option value="mission">Honoraires de mission</option>
                            <option value="finalisation">Accompagnement &amp; finalisation</option>
                        </select>
                    </div>
                    <div>
                        <label className="block text-[10px] tracking-[0.3em] uppercase text-[#d4af37]/70 mb-2">
                            Note (optionnel)
                        </label>
                        <input
                            data-testid="invoice-note"
                            value={draft.note}
                            onChange={(e) => setDraft({ ...draft, note: e.target.value })}
                            placeholder="Référence, lien, contexte…"
                            className="w-full bg-transparent border-b border-white/10 focus:border-[#d4af37] outline-none py-2 text-[13px] font-serif text-[#f4ecd8]/85"
                        />
                    </div>
                </div>
                <div className="mt-7 flex flex-wrap gap-3 justify-end">
                    {editId && (
                        <button
                            type="button"
                            onClick={cancelEdit}
                            className="px-5 py-2.5 text-[10px] tracking-[0.28em] uppercase border border-white/15 text-[#f4ecd8]/60 hover:text-[#f4ecd8] hover:border-white/30 transition-all"
                        >
                            Annuler
                        </button>
                    )}
                    <button
                        type="submit"
                        data-testid="invoice-submit"
                        disabled={busy || !draft.label.trim() || !draft.amount}
                        className="inline-flex items-center gap-2 px-5 py-2.5 text-[10px] tracking-[0.28em] uppercase border border-[#d4af37]/40 text-[#d4af37] hover:bg-[#d4af37] hover:text-[#070d18] transition-all duration-500 disabled:opacity-40 disabled:cursor-not-allowed"
                    >
                        <Plus strokeWidth={1.2} size={12} />
                        {editId ? "Enregistrer" : "Ajouter la facture"}
                    </button>
                </div>
            </form>

            {/* Invoice list */}
            <div data-testid="invoice-list">
                {invoices.length === 0 ? (
                    <p className="text-[12px] text-[#f4ecd8]/40 italic font-light py-6">
                        Aucune facture pour ce dossier. Ajoutez votre premier appel d'honoraires ci-dessus.
                    </p>
                ) : (
                    <div className="border-t border-white/[0.06]">
                        <div className="hidden md:grid grid-cols-[1fr_140px_140px_160px_auto] gap-4 py-3 text-[10px] tracking-[0.26em] uppercase text-[#d4af37]/60 border-b border-white/[0.08]">
                            <span>Libellé</span>
                            <span className="text-right">Montant</span>
                            <span className="text-right">Date</span>
                            <span>Statut</span>
                            <span></span>
                        </div>
                        {[...invoices]
                            .sort((a, b) =>
                                (b.date || "").localeCompare(a.date || ""),
                            )
                            .map((inv) => {
                                const meta = STATUS_META[inv.status] || STATUS_META.pending;
                                const StatusIcon = meta.Icon;
                                return (
                                    <div
                                        key={inv.id}
                                        data-testid={`invoice-row-${inv.id}`}
                                        className="grid grid-cols-[1fr_auto] md:grid-cols-[1fr_140px_140px_160px_auto] gap-3 md:gap-4 py-4 border-b border-white/[0.04] items-center"
                                    >
                                        <div className="min-w-0">
                                            <div className="font-serif text-[15px] text-[#f4ecd8] truncate">
                                                {inv.label}
                                            </div>
                                            {inv.note && (
                                                <div className="text-[11px] text-[#f4ecd8]/45 italic mt-1 truncate">
                                                    {inv.note}
                                                </div>
                                            )}
                                            <div className="md:hidden mt-2 text-[12px] text-[#d4af37]/80 tabular-nums">
                                                {formatEUR(inv.amount)} ·{" "}
                                                {inv.date
                                                    ? new Date(inv.date).toLocaleDateString("fr-FR")
                                                    : "—"}
                                            </div>
                                        </div>
                                        <span className="hidden md:block text-right font-serif text-[15px] text-[#d4af37] tabular-nums">
                                            {formatEUR(inv.amount)}
                                        </span>
                                        <span className="hidden md:block text-right text-[13px] text-[#f4ecd8]/65 tabular-nums">
                                            {inv.date
                                                ? new Date(inv.date).toLocaleDateString("fr-FR")
                                                : "—"}
                                        </span>
                                        <div className="flex items-center gap-1.5 flex-wrap">
                                            {["pending", "sent", "paid"].map((st) => (
                                                <button
                                                    key={st}
                                                    type="button"
                                                    data-testid={`invoice-${inv.id}-status-${st}`}
                                                    onClick={() => setStatusInline(inv, st)}
                                                    className={`text-[9px] tracking-[0.22em] uppercase px-2 py-1 border transition-all ${
                                                        inv.status === st
                                                            ? "border-[#d4af37]/60 text-[#d4af37] bg-[#d4af37]/10"
                                                            : "border-white/10 text-[#f4ecd8]/40 hover:border-white/25 hover:text-[#f4ecd8]/70"
                                                    }`}
                                                >
                                                    {STATUS_META[st].l}
                                                </button>
                                            ))}
                                        </div>
                                        <div className="col-span-2 md:col-span-1 flex items-center justify-end gap-2">
                                            <button
                                                type="button"
                                                onClick={() => startEdit(inv)}
                                                className="text-[10px] tracking-[0.24em] uppercase px-2 py-1 text-[#f4ecd8]/55 hover:text-[#d4af37] transition-colors"
                                            >
                                                Éditer
                                            </button>
                                            <button
                                                type="button"
                                                data-testid={`invoice-delete-${inv.id}`}
                                                onClick={() => removeInvoice(inv.id)}
                                                className="text-[#f4ecd8]/30 hover:text-[#e89090] transition-colors"
                                            >
                                                <Trash2 strokeWidth={1.2} size={14} />
                                            </button>
                                        </div>
                                    </div>
                                );
                            })}
                    </div>
                )}
            </div>
        </section>
    );
}

function Kpi({ label, sub, value, accent, Icon, testid }) {
    return (
        <div
            data-testid={testid}
            className="bg-[#0b1220] p-6 sm:p-7"
        >
            <div className="flex items-start justify-between mb-4">
                <span
                    className="text-[10px] tracking-[0.26em] uppercase"
                    style={{ color: `${accent}cc` }}
                >
                    {label}
                </span>
                <Icon
                    strokeWidth={1}
                    size={16}
                    style={{ color: `${accent}88` }}
                />
            </div>
            <div
                className="font-serif text-[22px] sm:text-[28px] leading-none text-[#f4ecd8] mb-2 tabular-nums"
                style={{ fontWeight: 300 }}
            >
                {formatEUR(value)}
            </div>
            <p className="text-[11px] text-[#f4ecd8]/40 tracking-wide italic">
                {sub}
            </p>
        </div>
    );
}

// ─────────────────────────────────────────────────────────────
// OpportunitiesPanel — future opportunities + development potential
// ─────────────────────────────────────────────────────────────

export function OpportunitiesPanel({ dossier, onDossierUpdate }) {
    const [options, setOptions] = useState([]);
    const [text, setText] = useState(dossier.future_opportunities || "");
    const [selected, setSelected] = useState(dossier.development_potential || []);
    const [busy, setBusy] = useState(false);
    const [msg, setMsg] = useState("");

    useEffect(() => {
        cockpit
            .get("/development-potential-options")
            .then(({ data }) => setOptions(data.options || []))
            .catch(() => setOptions([]));
    }, []);

    useEffect(() => {
        setText(dossier.future_opportunities || "");
        setSelected(dossier.development_potential || []);
    }, [dossier]);

    const toggle = (opt) => {
        setSelected((cur) =>
            cur.includes(opt) ? cur.filter((v) => v !== opt) : [...cur, opt],
        );
    };

    const save = async () => {
        setBusy(true);
        setMsg("");
        try {
            const payload = {
                name: dossier.name,
                sector: dossier.sector || "",
                geography: dossier.geography || "",
                operation_type: dossier.operation_type || "",
                maturity: dossier.maturity || "",
                ticket: dossier.ticket || "",
                confidentiality: dossier.confidentiality || "restricted",
                status: dossier.status || "draft",
                summary: dossier.summary || "",
                revenue_target: Number(dossier.revenue_target) || 0,
                future_opportunities: text,
                development_potential: selected,
            };
            const { data } = await cockpit.put(
                `/dossiers/${dossier.id}`,
                payload,
            );
            if (onDossierUpdate) onDossierUpdate(data);
            setMsg("Opportunités enregistrées ✓");
            setTimeout(() => setMsg(""), 2500);
        } catch (e) {
            setMsg(e?.response?.data?.detail || "Erreur d'enregistrement");
        } finally {
            setBusy(false);
        }
    };

    return (
        <section
            data-testid="cockpit-dossier-opportunities"
            className="border-t border-white/[0.06] py-12"
        >
            <div className="flex items-end justify-between mb-8 flex-wrap gap-4">
                <div>
                    <div className="overline text-[#d4af37]/70 mb-3">
                        Vision &amp; développement
                    </div>
                    <h2
                        className="font-serif text-[24px] text-[#f4ecd8]"
                        style={{ fontWeight: 400 }}
                    >
                        <Compass
                            strokeWidth={1}
                            size={18}
                            className="inline-block -mt-1 mr-2 text-[#d4af37]"
                        />
                        Opportunités futures
                    </h2>
                    <p className="mt-2 text-[12px] text-[#f4ecd8]/45 italic font-light max-w-2xl">
                        Note libre et axes de développement à explorer sur ce dossier.
                        Cette zone reste confidentielle, visible uniquement côté Cockpit.
                    </p>
                </div>
            </div>

            <div className="grid lg:grid-cols-12 gap-10">
                <div className="lg:col-span-7">
                    <label className="block text-[10px] tracking-[0.3em] uppercase text-[#d4af37]/70 mb-3">
                        Commentaires &amp; opportunités identifiées
                    </label>
                    <textarea
                        data-testid="opportunities-text"
                        rows={9}
                        value={text}
                        onChange={(e) => setText(e.target.value)}
                        placeholder="Notez ici les pistes de développement, les conversations récentes, les leads stratégiques liés à ce dossier…"
                        className="w-full bg-transparent border border-white/15 focus:border-[#d4af37] outline-none p-4 text-[14px] leading-[1.85] font-serif text-[#f4ecd8] placeholder-[#f4ecd8]/25 resize-y"
                    />
                </div>

                <div className="lg:col-span-5">
                    <label className="block text-[10px] tracking-[0.3em] uppercase text-[#d4af37]/70 mb-4">
                        Potentiel de développement d'affaires
                    </label>
                    <div className="flex flex-wrap gap-2">
                        {options.map((opt) => {
                            const active = selected.includes(opt);
                            return (
                                <button
                                    key={opt}
                                    type="button"
                                    data-testid={`dev-potential-${opt}`}
                                    onClick={() => toggle(opt)}
                                    className={`text-[11px] tracking-[0.18em] uppercase px-3.5 py-2 border transition-all duration-500 ${
                                        active
                                            ? "border-[#d4af37] bg-[#d4af37]/15 text-[#d4af37]"
                                            : "border-white/15 text-[#f4ecd8]/55 hover:border-[#d4af37]/40 hover:text-[#f4ecd8]"
                                    }`}
                                >
                                    {opt}
                                </button>
                            );
                        })}
                    </div>
                    {selected.length > 0 && (
                        <p className="mt-5 text-[11px] text-[#d4af37]/70 tracking-wide">
                            {selected.length} axe{selected.length > 1 ? "s" : ""} sélectionné{selected.length > 1 ? "s" : ""}
                        </p>
                    )}
                </div>
            </div>

            <div className="mt-8 flex items-center justify-between flex-wrap gap-4">
                {msg && (
                    <div
                        data-testid="opportunities-msg"
                        className="text-[12px] text-[#d4af37]/85 border-l-2 border-[#d4af37] pl-3 py-1 italic"
                    >
                        {msg}
                    </div>
                )}
                <button
                    type="button"
                    data-testid="opportunities-save"
                    onClick={save}
                    disabled={busy}
                    className="ml-auto inline-flex items-center gap-2 px-6 py-3 text-[10px] tracking-[0.3em] uppercase border border-[#d4af37]/40 text-[#d4af37] hover:bg-[#d4af37] hover:text-[#070d18] transition-all duration-500 disabled:opacity-40 disabled:cursor-not-allowed"
                >
                    {busy ? "Enregistrement…" : "Enregistrer"}
                </button>
            </div>
        </section>
    );
}
